import { createApi } from '@reduxjs/toolkit/query/react';

import { apiSahStatements } from '~constants/api';

import { Paginated, Request, rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

import { Statement } from '~support-at-home/models';

export const typePrefix = 'statements';

export interface StatementsRequest {
  client_ids: number[];
  start_date: string;
  end_date: string;
  include_empty?: boolean;
  include_deceased?: boolean;
}

export interface EstimateResponse {
  statements: {
    to_generate: number;
    existing: number;
    invalid: number;
  };
  items: {
    in_range: number;
    carried_over: number;
    invalid: number;
  };
  errors: ErrorItem[];
  warnings: WarningItem[];
}
export interface GenerateResponse {
  statements_generated: number;
  items_updated: number;
  statements: {
    statement_id: string;
    client_id: number;
    items_count: number;
  }[];
}

export interface ErrorItem {
  id: string;
  ref: string | null;
  reason: string;
  details: string;
  client_id?: number;
  client_name?: string;
  service_date?: string;
}

export interface WarningItem {
  id: number;
  client_name: string;
  reason: string;
  details: string;
}

export const statementsApi = createApi({
  reducerPath: 'statementsApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiSahStatements }),
  tagTypes: ['statements', 'estimates'],
  endpoints: (builder) => ({
    getStatements: builder.query<Paginated<Statement>, Request | void>({
      query: (params) => ({ url: '', method: 'GET', params }),
      providesTags: [{ type: 'statements', id: 'LIST' }],
      transformResponse: (payload) => unwrap<Paginated<Statement>>(payload),
      // Offset-based load-more: keep a single cache entry per filter set and
      // upsert successive pages into it. Excludes `offset` from the cache key
      // so paging the same filter set merges rather than replaces.
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        const { offset: _offset, ...stable } = (queryArgs ?? {}) as Record<string, unknown>;
        return { endpointName, ...stable };
      },
      merge: (currentCache, incoming, { arg }) => {
        const offset = Number((arg as { offset?: number } | undefined)?.offset) || 0;
        if (offset === 0) {
          return incoming;
        }
        const byId = new Map<string, Statement>();
        for (const row of currentCache.rows) byId.set(String(row.id), row);
        for (const row of incoming.rows) byId.set(String(row.id), row);
        const rows = Array.from(byId.values());
        // Preserve cached total when paging past the end returns no new rows —
        // the Load More button driving offset relies on a stable total.
        const total = incoming.rows.length > 0 ? incoming.total : currentCache.total;
        return { ...incoming, rows, count: incoming.count, total };
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        // serializeQueryArgs collapses offset into the cache key, so RTK Query
        // sees consecutive Load More clicks as the same subscription and skips
        // the fetch. Force the refetch whenever any arg (including offset)
        // changes — merge handles whether to replace or append.
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
    }),
    getStatement: builder.query<Statement, Request & { id: string }>({
      query: ({ id, ...rest }) => ({ url: `/${id}`, method: 'GET', params: rest }),
      providesTags: (_result, _error, { id }) => [{ type: 'statements', id }],
      transformResponse: (payload) => unwrap<Statement>(payload),
    }),
    estimate: builder.query<EstimateResponse, StatementsRequest>({
      query: (params) => ({ url: '/estimate', method: 'GET', params }),
      providesTags: [{ type: 'estimates', id: 'LIST' }],
      transformResponse: (payload) => unwrap<EstimateResponse>(payload),
    }),
    generate: builder.mutation<GenerateResponse, StatementsRequest>({
      query: (body) => ({ url: '/generate', method: 'POST', body }),
      invalidatesTags: [
        { type: 'statements', id: 'LIST' },
        { type: 'estimates', id: 'LIST' },
      ],
      transformResponse: (payload) => unwrap<GenerateResponse>(payload),
    }),
  }),
});

export const { useGetStatementsQuery, useGetStatementQuery, useEstimateQuery, useGenerateMutation } = statementsApi;
